import { useState, useEffect } from "react";
import { useAuth, apiClient } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Calendar, 
  Plus, 
  Clock,
  User,
  CheckCircle,
  XCircle,
  AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

export default function AppointmentsPage() {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [newAppointment, setNewAppointment] = useState({
    patient_id: "",
    clinic_id: "",
    appointment_type: "hearing_test",
    scheduled_at: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [apptsRes, patientsRes, clinicsRes] = await Promise.all([
        apiClient.get("/appointments"),
        apiClient.get("/patients"),
        apiClient.get("/clinics")
      ]);
      setAppointments(apptsRes.data);
      setPatients(patientsRes.data);
      setClinics(clinicsRes.data);
      if (clinicsRes.data.length > 0) {
        setNewAppointment(prev => ({ ...prev, clinic_id: clinicsRes.data[0].id }));
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load appointments");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...newAppointment,
        scheduled_at: selectedDate.toISOString()
      };
      const response = await apiClient.post("/appointments", payload);
      setAppointments([response.data, ...appointments]);
      toast.success("Appointment scheduled!");
      setDialogOpen(false);
      setNewAppointment({
        patient_id: "",
        clinic_id: clinics[0]?.id || "",
        appointment_type: "hearing_test",
        scheduled_at: "",
        notes: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create appointment");
    }
  };

  const updateAppointmentStatus = async (id, status) => {
    try {
      await apiClient.put(`/appointments/${id}/status?status=${status}`);
      setAppointments(appointments.map(a => 
        a.id === id ? { ...a, status } : a
      ));
      toast.success(`Appointment ${status}`);
    } catch (error) {
      toast.error("Failed to update appointment");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: "bg-blue-100 text-blue-700",
      confirmed: "bg-green-100 text-green-700",
      completed: "bg-slate-100 text-slate-700",
      cancelled: "bg-red-100 text-red-700",
      no_show: "bg-amber-100 text-amber-700"
    };
    return <Badge className={styles[status] || styles.scheduled}>{status}</Badge>;
  };

  const getPatientName = (patientId) => {
    const patient = patients.find(p => p.id === patientId);
    return patient?.name || "Unknown Patient";
  };

  const appointmentTypes = [
    { value: "hearing_test", label: "Hearing Test" },
    { value: "consultation", label: "Consultation" },
    { value: "trial", label: "Device Trial" },
    { value: "fitting", label: "Fitting Session" },
    { value: "follow_up", label: "Follow Up" }
  ];

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="appointments-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Appointments</h1>
            <p className="text-slate-500">Manage patient appointments and schedules</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-sky-600 hover:bg-sky-700" data-testid="add-appointment-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Appointment
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Schedule Appointment</DialogTitle>
                <DialogDescription>Book a new appointment for a patient</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateAppointment} className="space-y-4 mt-4">
                <div>
                  <Label>Patient *</Label>
                  <Select 
                    value={newAppointment.patient_id} 
                    onValueChange={(v) => setNewAppointment({...newAppointment, patient_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select patient" />
                    </SelectTrigger>
                    <SelectContent>
                      {patients.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Appointment Type *</Label>
                  <Select 
                    value={newAppointment.appointment_type} 
                    onValueChange={(v) => setNewAppointment({...newAppointment, appointment_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {appointmentTypes.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date & Time *</Label>
                  <CalendarComponent
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    className="rounded-md border mt-2"
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Input
                    value={newAppointment.notes}
                    onChange={(e) => setNewAppointment({...newAppointment, notes: e.target.value})}
                    placeholder="Additional notes..."
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-sky-600 hover:bg-sky-700"
                    disabled={!newAppointment.patient_id}
                  >
                    Schedule
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {["scheduled", "confirmed", "completed", "cancelled"].map(status => {
            const count = appointments.filter(a => a.status === status).length;
            const icons = {
              scheduled: Clock,
              confirmed: CheckCircle,
              completed: CheckCircle,
              cancelled: XCircle
            };
            const colors = {
              scheduled: "bg-blue-100 text-blue-600",
              confirmed: "bg-green-100 text-green-600",
              completed: "bg-slate-100 text-slate-600",
              cancelled: "bg-red-100 text-red-600"
            };
            const Icon = icons[status];
            return (
              <Card key={status}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[status]}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{count}</p>
                    <p className="text-sm text-slate-500 capitalize">{status}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Appointments List */}
        <Card>
          <CardHeader>
            <CardTitle>All Appointments</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="skeleton h-20 rounded-lg"></div>
                ))}
              </div>
            ) : appointments.length > 0 ? (
              <div className="space-y-3">
                {appointments.map(appt => (
                  <div 
                    key={appt.id}
                    className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                    data-testid={`appointment-${appt.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-sky-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">
                          {getPatientName(appt.patient_id)}
                        </p>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          <span className="capitalize">{appt.appointment_type.replace("_", " ")}</span>
                          <span>•</span>
                          <span>{format(new Date(appt.scheduled_at), "PPp")}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(appt.status)}
                      {appt.status === "scheduled" && (
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-green-600"
                            onClick={() => updateAppointmentStatus(appt.id, "confirmed")}
                          >
                            Confirm
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-600"
                            onClick={() => updateAppointmentStatus(appt.id, "cancelled")}
                          >
                            Cancel
                          </Button>
                        </div>
                      )}
                      {appt.status === "confirmed" && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => updateAppointmentStatus(appt.id, "completed")}
                        >
                          Mark Complete
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No appointments scheduled</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
